import React from "react";

const SparkleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 21 21" fill="none">
    <path
      d="M16.625 0.875L15.5225 3.28125L13.125 4.375L15.5225 5.4775L16.625 7.875L17.7188 5.4775L20.125 4.375L17.7188 3.28125M7.875 3.5L5.6875 8.3125L0.875 10.5L5.6875 12.6875L7.875 17.5L10.0625 12.6875L14.875 10.5L10.0625 8.3125M16.625 13.125L15.5225 15.5225L13.125 16.625L15.5225 17.7188L16.625 20.125L17.7188 17.7188L20.125 16.625L17.7188 15.5225"
      fill="#8B5CF6"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);


const getVerdictConfig = (score) => {
  if (score >= 80)
    return { label: "Strong Position", color: "#258F40", bgColor: "#EBF7ED", icon: "check" };
  if (score >= 50)
    return { label: "Proceed With Caution", color: "#D97706", bgColor: "#FFFBEB", icon: "alert" };
  return { label: "Proceed With Caution", color: "#DC2626", bgColor: "#FEF2F2", icon: "x" };
};

// Shield glyph whose inner mark (✓ / ! / ✕) reflects the verdict tier.
const ShieldVerdictIcon = ({ type, color }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="flex-shrink-0"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    {type === "check" && <polyline points="9 12 11 14 15 10" />}
    {type === "alert" && (
      <>
        <line x1="12" y1="8" x2="12" y2="12.5" />
        <line x1="12" y1="15.5" x2="12.01" y2="15.5" />
      </>
    )}
    {type === "x" && (
      <>
        <line x1="9.5" y1="9.5" x2="14.5" y2="14.5" />
        <line x1="14.5" y1="9.5" x2="9.5" y2="14.5" />
      </>
    )}
  </svg>
);

/**
 * Popover under the header "View Analysis" button. Shows the requirement
 * alignment verdict; "Re-Analyze" re-runs POST /requirements/alignment/.
 */
const BidReadinessAnalysisModal = ({ isOpen, onClose, anchor, result, onRerun, isAnalyzing }) => {
  if (!isOpen) return null;

  const isDone = result?.alignment_status === "done";
  // Prefer the priority-weighted fit (the authoritative metric, recomputed on
  // every status toggle) so the verdict matches the recommendation text below.
  const score = result?.weighted_fit_pct ?? result?.alignment_score ?? 0;
  const cfg = getVerdictConfig(score);

  const popStyle = {
    top: `${anchor?.top ?? 70}px`,
    right: `${anchor?.right ?? 16}px`,
  };

  return (
    <>
      {/* transparent click-catcher to close on outside click */}
      <div className="fixed inset-0 z-[999]" onClick={onClose} />

      <div
        className="fixed z-[1000] w-[400px] bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.12)] border border-[#E5E5E5] flex flex-col font-outfit text-left"
        style={popStyle}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 bg-[#F0E5FF52] py-2 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <SparkleIcon />
            <span className="font-semibold text-[16.5px] text-[#8B55DD]">AI Analysis Result</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onRerun}
              disabled={isAnalyzing}
              className="text-[13px] font-medium text-[#8B55DD] underline hover:opacity-80 disabled:opacity-50"
            >
              {isAnalyzing ? "Analyzing…" : "Re-Analyze"}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-3">
          {isAnalyzing ? (
            <div className="flex items-center gap-3 px-1 py-6">
              <div className="w-6 h-6 rounded-full border-[3px] border-[#E9D5FF] border-t-[#8B5CF6] animate-spin shrink-0" />
              <span className="text-[13px] text-[#767676]">Analyzing requirements…</span>
            </div>
          ) : !isDone ? (
            <div className="px-1 py-4 text-[13px] text-[#767676]">
              No analysis yet. Click <span className="font-medium text-[#8B55DD]">Re-Analyze</span> to run a requirement analysis.
            </div>
          ) : (
            <div
              className="rounded-[8px] p-3 border"
              style={{ backgroundColor: cfg.bgColor, borderColor: `${cfg.color}33` }}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <ShieldVerdictIcon type={cfg.icon} color={cfg.color} />
                <span className="text-[16px] font-semibold" style={{ color: cfg.color }}>
                  {cfg.label}
                </span>
              </div>
              <p className="text-[13.5px] text-[#000000B3] leading-snug">
                Meets most requirements. Safe to proceed with this Bid.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BidReadinessAnalysisModal;
